import * as React from "react";
import { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Paper,
  Grid,
  Link,
  IconButton,
  Tooltip,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from "@material-ui/core";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import SearchIcon from "@material-ui/icons/Search";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import WarningIcon from "@material-ui/icons/Warning";
import { makeStyles } from "@material-ui/core/styles";
import axios from "axios";
import { useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigPost } from "../getConfig";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundColor: "#f5f7fa",
    minHeight: "100vh",
    padding: "3em 2em",
    boxSizing: "border-box",
    width: "100%",
    overflow: "hidden",
  },
  container: {
    width: "100%",
    maxWidth: "800px",
    boxSizing: "border-box",
    padding: "0 1em",
    minWidth: 0,
    overflow: "hidden",
  },
  pageTitle: {
    marginBottom: "2em",
    textAlign: "center",
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: "2rem",
  },
  card: {
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    borderRadius: "12px",
    marginBottom: "2em",
  },
  form: {
    padding: "2em",
  },
  formField: {
    marginBottom: "1.5em",
    "& .MuiInputBase-root": {
      backgroundColor: "white",
    },
  },
  submitButton: {
    padding: "12px 48px",
    fontSize: "1rem",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(63, 81, 181, 0.3)",
    transition: "all 0.3s ease",
    marginTop: "1em",
    "&:hover": {
      boxShadow: "0 6px 16px rgba(63, 81, 181, 0.4)",
      transform: "translateY(-2px)",
    },
  },
  proceedButton: {
    padding: "12px 48px",
    fontSize: "1rem",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
    transition: "all 0.3s ease",
    marginTop: "1em",
    backgroundColor: "#4caf50",
    color: "white",
    "&:hover": {
      boxShadow: "0 6px 16px rgba(76, 175, 80, 0.4)",
      transform: "translateY(-2px)",
      backgroundColor: "#43a047",
    },
  },
  resultCard: {
    marginTop: "2em",
    padding: "2em",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderRadius: "16px",
    boxShadow: "0 8px 24px rgba(102, 126, 234, 0.4)",
    color: "white",
  },
  transactionCard: {
    marginTop: "2em",
    padding: "2em",
    background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
    borderRadius: "16px",
    boxShadow: "0 8px 24px rgba(17, 153, 142, 0.4)",
    color: "white",
  },
  warningCard: {
    marginTop: "2em",
    padding: "2em",
    background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    borderRadius: "16px",
    boxShadow: "0 8px 24px rgba(245, 87, 108, 0.4)",
    color: "white",
  },
  resultTitle: {
    color: "rgba(255,255,255,0.9)",
    marginBottom: "1em",
    fontSize: "1.2rem",
    fontWeight: 600,
  },
  resultContent: {
    color: "white",
  },
  resultLink: {
    color: "#90caf9",
    textDecoration: "underline",
    fontWeight: 400,
    wordBreak: "break-all" as const,
    "&:hover": {
      color: "#bbdefb",
    },
  },
  linkContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  copyButton: {
    color: "white",
    padding: "4px",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.1)",
    },
  },
  resultItem: {
    marginBottom: "1em",
    padding: "0.75em",
    backgroundColor: "rgba(255,255,255,0.1)",
    borderRadius: "8px",
  },
  resultLabel: {
    color: "rgba(255,255,255,0.9)",
    fontSize: "0.9rem",
    marginBottom: "0.25em",
  },
  resultValue: {
    color: "white",
    fontSize: "1.1rem",
    fontWeight: 600,
  },
  infoBox: {
    marginBottom: "2em",
    padding: "1.5em",
    backgroundColor: "#e3f2fd",
    borderRadius: "8px",
    borderLeft: "4px solid #2196f3",
    wordWrap: "break-word",
    overflowWrap: "break-word",
    maxWidth: "100%",
    boxSizing: "border-box",
    width: "100%",
    minWidth: 0,
    overflow: "hidden",
    display: "block",
  },
  infoText: {
    color: "#1565c0",
    fontSize: "0.95rem",
    wordWrap: "break-word",
    overflowWrap: "break-word",
    wordBreak: "break-word",
    whiteSpace: "normal",
    width: "100%",
    maxWidth: "100%",
    display: "block",
    lineHeight: "1.6",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    hyphens: "auto",
  },
  statusChip: {
    fontWeight: 600,
    marginLeft: "8px",
  },
  chipSuccess: {
    backgroundColor: "#4caf50",
    color: "white",
  },
  chipError: {
    backgroundColor: "#f44336",
    color: "white",
  },
  chipWarning: {
    backgroundColor: "#ff9800",
    color: "white",
  },
  gridRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
  },
  inlineItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "0.5em",
  },
  inlineLabel: {
    color: "rgba(255,255,255,0.85)",
    fontSize: "0.9rem",
    fontWeight: 500,
  },
  inlineValue: {
    color: "white",
    fontSize: "1rem",
    fontWeight: 600,
  },
  compactRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "24px",
    marginBottom: "1em",
    padding: "0.75em",
    backgroundColor: "rgba(255,255,255,0.1)",
    borderRadius: "8px",
  },
}));

interface TransactionDetails {
  transaction_id: string;
  gift_card_user_payment_id: number;
  amount: number;
  status: string;
  brand: string | null;
  user_name: string | null;
  sprive_cancelled: number;
  is_refunded: number;
  refund_exists: boolean;
  existing_refund: {
    refund_type: string;
    provider: string;
    created_at: string;
  } | null;
}

interface PayoutLinkResult {
  refund_type: 'payout_link';
  url: string;
  amount: number;
  name: string;
}

interface BankTransferResult {
  refund_type: 'bank_transfer';
  amount: number;
  payment_id: string;
  status: string;
  validation_result: string;
  sort_code: string;
  account_number: string;
  bank_name: string;
  provider_id: string;
  reference: string;
  request_id: string;
}

type RefundResult = PayoutLinkResult | BankTransferResult;

interface TransactionApiResponse {
  status: boolean;
  message: string;
  data: TransactionDetails;
  meta: object;
}

interface RefundApiResponse {
  status: boolean;
  message: string;
  data: RefundResult;
  meta: object;
}

type RefundType = 'bank_transfer' | 'payout_link';

const RevolutRefundDashboard = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [fetchLoading, setFetchLoading] = useState(false);
  const [refundLoading, setRefundLoading] = useState(false);
  const [transactionId, setTransactionId] = useState("");
  const [name, setName] = useState("");
  const [refundType, setRefundType] = useState<RefundType>('bank_transfer');
  const [transactionDetails, setTransactionDetails] = useState<TransactionDetails | null>(null);
  const [result, setResult] = useState<RefundResult | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [bankTransferFailed, setBankTransferFailed] = useState(false);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!transactionId.trim()) {
      newErrors.transactionId = "Transaction ID is required";
    }

    // Name is optional - backend will fetch from user table if not provided

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Step 1: Fetch transaction details
  const handleFetchTransaction = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      notify("Please fix the errors in the form", "error");
      return;
    }

    try {
      setFetchLoading(true);
      setTransactionDetails(null);
      setResult(null);

      const config = getConfigPost();
      let url = `${ENDPOINTS.REVOLUT_REFUND}?transaction_id=${encodeURIComponent(transactionId.trim())}`;
      if (name.trim()) {
        url += `&name=${encodeURIComponent(name.trim())}`;
      }
      const response = await axios.get<TransactionApiResponse>(
        url,
        { headers: config }
      );

      // Handle both wrapped ({ data: {...} }) and direct response formats
      const transactionData = response.data.data || (response.data as unknown as TransactionDetails);
      setTransactionDetails(transactionData);
      notify("Transaction details fetched successfully!", "info");
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Failed to fetch transaction details";
      notify(errorMessage, "error");
      setTransactionDetails(null);
    } finally {
      setFetchLoading(false);
    }
  };

  // Step 2: Proceed with refund
  const handleProceedRefund = async () => {
    if (!transactionDetails) {
      notify("Please fetch transaction details first", "error");
      return;
    }

    try {
      setRefundLoading(true);

      const config = getConfigPost();
      const payload: { transaction_id: string; name?: string; refund_type: RefundType } = {
        transaction_id: transactionId.trim(),
        refund_type: refundType,
      };

      // Only include name if provided
      if (name.trim()) {
        payload.name = name.trim();
      }

      const response = await axios.post<RefundApiResponse>(ENDPOINTS.REVOLUT_REFUND, payload, {
        headers: config,
      });

      // Handle both wrapped ({ data: {...} }) and direct response formats
      const refundData = response.data.data || (response.data as unknown as RefundResult);
      setResult(refundData);
      setBankTransferFailed(false);
      
      if (refundType === 'bank_transfer') {
        notify("Bank transfer refund processed successfully!", "info");
      } else {
        notify("Revolut payout link generated successfully!", "info");
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Failed to process refund";
      notify(errorMessage, "error");
      setResult(null);
      
      // Track if bank transfer failed to suggest payout link
      if (refundType === 'bank_transfer') {
        setBankTransferFailed(true);
      }
    } finally {
      setRefundLoading(false);
    }
  };

  const handleReset = () => {
    setTransactionId("");
    setName("");
    setRefundType('bank_transfer');
    setTransactionDetails(null);
    setResult(null);
    setErrors({});
    setBankTransferFailed(false);
  };

  const handleSwitchToPayoutLink = () => {
    setRefundType('payout_link');
    setBankTransferFailed(false);
  };

  const isEligibleForRefund = transactionDetails && 
    transactionDetails.status === "executed" && 
    !transactionDetails.refund_exists;

  return (
    <div className={classes.main}>
      <div className={classes.container}>
        <Typography variant="h4" className={classes.pageTitle}>
          Revolut Refund
        </Typography>

        <Paper 
          className={classes.infoBox}
          style={{ 
            width: "100%", 
            maxWidth: "100%", 
            boxSizing: "border-box",
            overflow: "hidden",
            display: "block",
            minWidth: 0
          }}
        >
          <div style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", minWidth: 0 }}>
            <Typography 
              className={classes.infoText} 
              component="div"
              style={{
                width: "100%",
                maxWidth: "100%",
                wordBreak: "break-word",
                overflowWrap: "break-word",
                whiteSpace: "normal",
                display: "block",
                boxSizing: "border-box",
                minWidth: 0,
                margin: 0,
                padding: 0
              }}
            >
              <strong>Note:</strong> This tool generates a Revolut refund link or processes a bank transfer for cancelled transactions.
              <br />
              Enter the Transaction ID (SIMP_ID) to process the refund. The system validates that the transaction
              <br />
              is executed and cancelled (sprive_cancelled = 1). Name is optional - if not provided, it will be
              <br />
              fetched from the user's profile.
            </Typography>
          </div>
        </Paper>

        <Card className={classes.card}>
          <CardContent className={classes.form}>
            <form onSubmit={handleFetchTransaction}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    className={classes.formField}
                    label="Transaction ID (SIMP_ID)"
                    variant="outlined"
                    fullWidth
                    value={transactionId}
                    onChange={(e) => {
                      setTransactionId(e.target.value);
                      // Reset transaction details when transaction ID changes
                      if (transactionDetails) {
                        setTransactionDetails(null);
                        setResult(null);
                      }
                    }}
                    error={!!errors.transactionId}
                    helperText={errors.transactionId}
                    required
                    disabled={fetchLoading || refundLoading}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    className={classes.formField}
                    label="Name (Optional)"
                    variant="outlined"
                    fullWidth
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    helperText="Leave empty to use name from user's profile"
                    disabled={fetchLoading || refundLoading}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl component="fieldset" disabled={fetchLoading || refundLoading}>
                    <FormLabel component="legend" style={{ marginBottom: "8px", fontWeight: 500 }}>
                      Refund Type
                    </FormLabel>
                    <RadioGroup
                      row
                      value={refundType}
                      onChange={(e) => {
                        setRefundType(e.target.value as RefundType);
                        setBankTransferFailed(false);
                      }}
                    >
                      <FormControlLabel
                        value="bank_transfer"
                        control={<Radio color="primary" />}
                        label="Bank Transfer (Recommended)"
                      />
                      <FormControlLabel
                        value="payout_link"
                        control={<Radio color="primary" />}
                        label="Payout Link"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" justifyContent="center" flexWrap="wrap" style={{ gap: "16px" }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      className={classes.submitButton}
                      disabled={fetchLoading || refundLoading}
                      startIcon={fetchLoading ? <CircularProgress size={20} /> : <SearchIcon />}
                    >
                      {fetchLoading ? "Fetching..." : "Fetch Transaction Details"}
                    </Button>
                    {(transactionDetails || result) && (
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleReset}
                        disabled={fetchLoading || refundLoading}
                      >
                        Reset
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>

        {/* Transaction Details Card */}
        {transactionDetails && !result && (
          <Card className={transactionDetails.refund_exists ? classes.warningCard : classes.transactionCard}>
            <Typography variant="h6" className={classes.resultTitle}>
              Transaction Details
            </Typography>
            <div className={classes.resultContent}>
              {/* Compact row with Transaction ID, Brand, Amount */}
              <div className={classes.compactRow}>
                <div className={classes.inlineItem}>
                  <Typography className={classes.inlineLabel}>Transaction ID:</Typography>
                  <Typography className={classes.inlineValue}>
                    {transactionDetails.transaction_id}
                  </Typography>
                </div>
                {transactionDetails.user_name && (
                  <div className={classes.inlineItem}>
                    <Typography className={classes.inlineLabel}>User Name:</Typography>
                    <Typography className={classes.inlineValue}>
                      {transactionDetails.user_name}
                    </Typography>
                  </div>
                )}
                {transactionDetails.brand && (
                  <div className={classes.inlineItem}>
                    <Typography className={classes.inlineLabel}>Brand:</Typography>
                    <Typography className={classes.inlineValue}>
                      {transactionDetails.brand}
                    </Typography>
                  </div>
                )}
                <div className={classes.inlineItem}>
                  <Typography className={classes.inlineLabel}>Amount:</Typography>
                  <Typography className={classes.inlineValue}>
                    £{transactionDetails.amount?.toFixed(2) || "N/A"}
                  </Typography>
                </div>
              </div>

              {/* Status chips row */}
              <div className={classes.compactRow}>
                <div className={classes.inlineItem}>
                  <Typography className={classes.inlineLabel}>Status:</Typography>
                  <Chip
                    size="small"
                    label={transactionDetails.status}
                    className={`${classes.statusChip} ${
                      transactionDetails.status === "executed" ? classes.chipSuccess : classes.chipWarning
                    }`}
                    icon={transactionDetails.status === "executed" ? <CheckCircleIcon style={{ color: "white" }} /> : <WarningIcon style={{ color: "white" }} />}
                  />
                </div>
                <div className={classes.inlineItem}>
                  <Typography className={classes.inlineLabel}>Sprive Cancelled:</Typography>
                  <Chip
                    size="small"
                    label={transactionDetails.sprive_cancelled === 1 ? "Yes" : "No"}
                    className={`${classes.statusChip} ${
                      transactionDetails.sprive_cancelled === 1 ? classes.chipSuccess : classes.chipError
                    }`}
                    icon={transactionDetails.sprive_cancelled === 1 ? <CheckCircleIcon style={{ color: "white" }} /> : <ErrorIcon style={{ color: "white" }} />}
                  />
                </div>
                <div className={classes.inlineItem}>
                  <Typography className={classes.inlineLabel}>Is Refunded:</Typography>
                  <Chip
                    size="small"
                    label={transactionDetails.is_refunded === 1 ? "Yes" : "No"}
                    className={`${classes.statusChip} ${
                      transactionDetails.is_refunded === 1 ? classes.chipWarning : classes.chipSuccess
                    }`}
                  />
                </div>
              </div>

              {/* Bank Transfer Failed Warning */}
              {bankTransferFailed && (
                <div className={classes.resultItem} style={{ backgroundColor: "rgba(255,193,7,0.3)", marginBottom: "1em" }}>
                  <Typography style={{ color: "white", fontWeight: 600, marginBottom: "8px" }}>
                    <span role="img" aria-label="warning">⚠️</span> Bank Transfer Failed
                  </Typography>
                  <Typography style={{ color: "rgba(255,255,255,0.9)", fontSize: "0.95rem", marginBottom: "12px" }}>
                    The bank transfer could not be processed. This may be due to missing payment source details from TrueLayer.
                    Please try using the Payout Link option instead.
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    style={{ backgroundColor: "#ff9800", color: "white" }}
                    onClick={handleSwitchToPayoutLink}
                  >
                    Switch to Payout Link
                  </Button>
                </div>
              )}

              {/* Proceed Button */}
              <Box display="flex" justifyContent="center" marginTop="1.5em">
                {isEligibleForRefund ? (
                  <Button
                    variant="contained"
                    className={classes.proceedButton}
                    onClick={handleProceedRefund}
                    disabled={refundLoading}
                    startIcon={refundLoading ? <CircularProgress size={20} style={{ color: "white" }} /> : <CheckCircleIcon />}
                  >
                    {refundLoading 
                      ? "Processing Refund..." 
                      : refundType === 'bank_transfer' 
                        ? "Proceed with Bank Transfer" 
                        : "Generate Payout Link"}
                  </Button>
                ) : (
                  <Typography style={{ color: "rgba(255,255,255,0.9)", fontStyle: "italic" }}>
                    {transactionDetails.refund_exists
                      ? "❌ Cannot proceed: Refund already exists for this transaction"
                      : transactionDetails.status !== "executed"
                      ? `❌ Cannot proceed: Status must be 'executed' (current: ${transactionDetails.status})`
                      : "❌ Cannot proceed with refund"}
                  </Typography>
                )}
              </Box>

              {/* Existing Refund Warning - at the bottom */}
              {transactionDetails.refund_exists && transactionDetails.existing_refund && (
                <div className={classes.resultItem} style={{ backgroundColor: "rgba(255,255,255,0.2)", marginTop: "1.5em" }}>
                  <Typography className={classes.resultLabel} style={{ fontWeight: 600 }}>
                    <span role="img" aria-label="warning">⚠️</span> Existing Refund Found:
                  </Typography>
                  <Typography className={classes.resultValue} style={{ fontSize: "0.95rem" }}>
                    Type: {transactionDetails.existing_refund.refund_type || "N/A"} | Provider: {transactionDetails.existing_refund.provider}
                  </Typography>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Refund Result Card - Payout Link */}
        {result && result.refund_type === 'payout_link' && (
          <Card className={classes.resultCard}>
            <Typography variant="h6" className={classes.resultTitle}>
              Payout Link Generated Successfully
            </Typography>
            <div className={classes.resultContent}>
              {result.name && (
                <div className={classes.resultItem}>
                  <Typography className={classes.resultLabel}>Name:</Typography>
                  <Typography className={classes.resultValue}>
                    {result.name}
                  </Typography>
                </div>
              )}
              {result.amount != null && (
                <div className={classes.resultItem}>
                  <Typography className={classes.resultLabel}>Amount:</Typography>
                  <Typography className={classes.resultValue}>
                    £{result.amount.toFixed(2)}
                  </Typography>
                </div>
              )}
              <div className={classes.resultItem}>
                <Typography className={classes.resultLabel}>Refund URL:</Typography>
                <div className={classes.linkContainer}>
                  <Link
                    href={result.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.resultLink}
                  >
                    {result.url}
                  </Link>
                  <Tooltip title="Copy link">
                    <IconButton
                      className={classes.copyButton}
                      onClick={() => {
                        navigator.clipboard.writeText(result.url);
                        notify("Link copied to clipboard", "info");
                      }}
                      size="small"
                    >
                      <FileCopyOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Refund Result Card - Bank Transfer */}
        {result && result.refund_type === 'bank_transfer' && (
          <Card className={classes.transactionCard}>
            <Typography variant="h6" className={classes.resultTitle}>
              Bank Transfer Processed Successfully
            </Typography>
            <div className={classes.resultContent}>
              {/* Amount and Status row */}
              <div className={classes.compactRow}>
                <div className={classes.inlineItem}>
                  <Typography className={classes.inlineLabel}>Amount:</Typography>
                  <Typography className={classes.inlineValue}>
                    £{result.amount?.toFixed(2) || "N/A"}
                  </Typography>
                </div>
                <div className={classes.inlineItem}>
                  <Typography className={classes.inlineLabel}>Status:</Typography>
                  <Chip
                    size="small"
                    label={result.status}
                    className={`${classes.statusChip} ${
                      result.status === 'completed' ? classes.chipSuccess : classes.chipWarning
                    }`}
                    icon={<CheckCircleIcon style={{ color: "white" }} />}
                  />
                </div>
              </div>

              {/* Bank Details row */}
              <div className={classes.compactRow}>
                <div className={classes.inlineItem}>
                  <Typography className={classes.inlineLabel}>Bank Name:</Typography>
                  <Typography className={classes.inlineValue}>
                    {result.provider_id}
                  </Typography>
                </div>
                <div className={classes.inlineItem}>
                  <Typography className={classes.inlineLabel}>Sort Code:</Typography>
                  <Typography className={classes.inlineValue}>
                    {result.sort_code}
                  </Typography>
                </div>
                <div className={classes.inlineItem}>
                  <Typography className={classes.inlineLabel}>Account Number:</Typography>
                  <Typography className={classes.inlineValue}>
                    {result.account_number}
                  </Typography>
                </div>
                <Tooltip title="Copy bank details">
                  <IconButton
                    className={classes.copyButton}
                    onClick={() => {
                      const bankDetails = `Bank Name: ${result.provider_id}\nSort Code: ${result.sort_code}\nAccount Number: ${result.account_number}`;
                      navigator.clipboard.writeText(bankDetails);
                      notify("Bank details copied to clipboard", "info");
                    }}
                    size="small"
                  >
                    <FileCopyOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>

              {/* Reference and IDs row */}
              <div className={classes.compactRow}>
                <div className={classes.inlineItem}>
                  <Typography className={classes.inlineLabel}>Reference:</Typography>
                  <Typography className={classes.inlineValue}>
                    {result.reference}
                  </Typography>
                </div>
                <div className={classes.inlineItem}>
                  <Typography className={classes.inlineLabel}>Payment ID:</Typography>
                  <Typography className={classes.inlineValue} style={{ fontSize: "0.85rem" }}>
                    {result.payment_id}
                  </Typography>
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default RevolutRefundDashboard;

